/// <amd-module name="Wit/SprintsHub/SprintsView/Components/Taskboard/TaskboardRow" />
import { WorkItemStateCategory } from "Agile/Common/Redux/BacklogConfiguration/BacklogConfigurationContracts";
import { IObservableArray, IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { usePageContext, useService } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Dropdown, DropdownExpandableButton } from "VSSUI/Dropdown";
import { Icon, IconSize } from "VSSUI/Icon";
import { IListItemDetails } from "VSSUI/List";
import { IObservableExpression, Observer } from "VSSUI/Observer";
import { ITableColumn } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { ICardStyle, IClientWorkItem, IObservableClientWorkItem, WorkItemEvents } from "Wit/ClientWorkItemService/IClientWorkItem";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { DragMode, IPosition, ScrollableData } from "Wit/Common/Controls/Boards/Draggable";
import { OnChangeType } from "Wit/Common/Controls/Boards/EditableField";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { formatRemainingWorkForDisplay } from "Wit/Common/Utilities/FormatUtils";
import { unassignedId, unparentedId } from "Wit/SprintsHub/Common/Constants";
import { ISprintTaskboardData, ITaskboardWorkItem } from "Wit/SprintsHub/Common/Contracts/Interfaces";
import { TaskboardCard } from "Wit/SprintsHub/SprintsView/Components/Taskboard/TaskboardCard";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { ITaskboardService } from "Wit/SprintsHub/SprintsView/Services/ITaskboardService";
import { getWorkItemTypeMenuItems } from "Wit/SprintsHub/SprintsView/Utils/WorkItemTypesDropdownUtils";
import { WorkItemTypeColorAndIcon, WorkItemTypeIcon } from "Wit/UI/Components/WorkItemTypeIcon";
import { WorkItemTrackingDataProvider } from "Wit/UI/Data";
import { IdentityDisplayMode, IdentityView } from "Wit/UI/IdentityView";
import * as React from "react";
import { ITaskboardRow } from "./ITaskboardRow";
import { WorkItemArrayEvents } from "./Contracts/Contracts";

export interface ITaskboardRowProps {
    columns: ITableColumn<ITaskboardRow>[];
    item: ITaskboardRow;
    cardStyles: ICardStyle[];
    details: IListItemDetails<ITaskboardRow>;
    filteredTaskboardRows: IObservableArray<ITaskboardRow>;
    newItemType: IObservableValue<string>;
    rowIndex: number;
    rowHeights: number[];
    taskboardData: ISprintTaskboardData | undefined;
    newChildId: ObservableValue<number | undefined>;
    onNewChildSelected: (type: string, row: ITaskboardRow) => void;
    onParentItemCreate: (id: number) => void;
    onParentItemCancel: () => void;
    onCardChange: OnChangeType;
    onCardClick: (workItemId: number) => void;
    onCardDrag: (
        e: PointerEvent,
        card: HTMLElement,
        mode: DragMode,
        position: IPosition,
        workItem: IObservableClientWorkItem<IClientWorkItem>,
        startingColumnId: string,
        startingRowId: string
    ) => undefined;
    onChildItemCreate: (item: ITaskboardRow, child: IObservableClientWorkItem<IClientWorkItem>, originalId: number, enterPressed: boolean) => void;
    onChildItemCancel: (child: IObservableClientWorkItem<IClientWorkItem>) => void;
    onChildCardKeyDown: (
        event: React.KeyboardEvent<HTMLElement>,
        item: IObservableClientWorkItem<IClientWorkItem>,
        row: ITaskboardRow,
        rowIndex: number
    ) => void;
    onParentCardKeyDown: (
        event: React.KeyboardEvent<HTMLElement>,
        item: IObservableClientWorkItem<IClientWorkItem>,
        row: ITaskboardRow,
        rowIndex: number
    ) => void;
    orderByAssignedTo: boolean;
    previousNewChildId: number | undefined;
    scrollableData?: ScrollableData;
    someRowsExpanded: ObservableValue<boolean>;
    workItemTrackingDataProvider: WorkItemTrackingDataProvider;
    workItemTypeColorAndIcons: WorkItemTypeColorAndIcon[] | undefined;
}

interface IWorkItemStatusRollup {
    notStarted: number;
    inProgress: number;
    completed: number;
}

export const TaskboardRow: React.FunctionComponent<ITaskboardRowProps> = ({
    columns,
    filteredTaskboardRows,
    item,
    newChildId,
    newItemType,
    cardStyles,
    details,
    onCardChange,
    onCardClick,
    onCardDrag,
    onChildCardKeyDown,
    onChildItemCreate,
    onChildItemCancel,
    onNewChildSelected,
    onParentCardKeyDown,
    onParentItemCreate,
    onParentItemCancel,
    orderByAssignedTo,
    previousNewChildId,
    rowHeights,
    rowIndex,
    scrollableData,
    someRowsExpanded,
    taskboardData,
    workItemTrackingDataProvider,
    workItemTypeColorAndIcons
}) => {
    const pageContext = usePageContext();
    const readonlySelection = React.useRef(new DropdownSelection());
    readonlySelection.current.lock();
    const taskboardService = useService<ITaskboardService>("ITaskboardService");
    const workItemColumnIdMap = taskboardService.getWorkItemColumnMap();
    const rowRef = React.useRef<HTMLTableRowElement | null>(null);
    const setRowHeight = () => {
        if (rowRef.current) {
            rowHeights[rowIndex] = rowRef.current.clientHeight;
        }
    };
    const expandItem = (event: any) => {
        item.expanded.value = true;
        someRowsExpanded.value = true;
        event.stopPropagation();
    };
    const parentIdRef = React.useRef<number>();
    React.useLayoutEffect(setRowHeight, []);

    const onRowFocus = (event: React.FocusEvent<HTMLElement>) => {
        details.onFocusItem(rowIndex, event);
    };

    const isCardMovementOptimized = React.useMemo(() => {
        return isFeatureFlagEnabled(pageContext, FeatureFlags.CardMovementOptimization, false);
    }, []);

    const isCardSettingsOptimizationEnabled = React.useMemo(() => {
        return isFeatureFlagEnabled(pageContext, FeatureFlags.ApplySettingsToCardWhenGroupedByAssignedToOptimization, false);
    }, []);

    return (
        <Observer
            expanded={{
                observableValue: item.expanded,
                filter: () => {
                    // Allow the row to rerender before measuring the height.
                    window.setTimeout(() => {
                        setRowHeight();
                    }, 0);
                    return true;
                }
            }}
            parent={
                {
                    observableValue: item.parent,
                    filter: parent => parent?.id !== parentIdRef.current
                } as IObservableExpression<IObservableClientWorkItem<ITaskboardWorkItem> | undefined>
            }
        >
            {props => {
                parentIdRef.current = props.parent?.id;
                if (!props.expanded) {
                    const workStatusRollup = getWorkItemStateRollupCount(item.children.value, taskboardData!);
                    const notStartedString = workStatusRollup.notStarted ? format(Resources.NotStartedFormat, workStatusRollup.notStarted) : "";
                    const inProgressString = workStatusRollup.inProgress ? format(Resources.InProgressFormat, workStatusRollup.inProgress) : "";
                    const rowId = orderByAssignedTo
                        ? item.assignedTo?.displayName ?? unassignedId
                        : item.parent?.value?.id?.toString() ?? unparentedId.toString();
                    return (
                        <tr key={rowIndex} data-row-index={rowIndex} ref={rowRef}>
                            <td
                                className="taskboard-collapsed-row"
                                colSpan={columns.length + 1}
                                data-column={columns && columns.length ? columns[0].id : ""}
                                data-columname={columns && columns.length ? columns[0].name : ""}
                                data-row={rowId}
                                data-iscollapsed={true}
                            >
                                <div className="flex-row flex-center" onClick={expandItem}>
                                    <Button
                                        className="taskboard-expand-collapse-button"
                                        ariaLabel={Resources.ExpandRow}
                                        iconProps={{ iconName: "TriangleRight12", size: IconSize.small, className: "icon-extra-small" }}
                                        onClick={expandItem}
                                        subtle
                                    />

                                    {item.assignedTo ? (
                                        <IdentityView
                                            displayTooltipOnHover={true}
                                            identityRef={item.assignedTo}
                                            className="margin-top-8 text-ellipsis"
                                            viewMode={IdentityDisplayMode.AvatarAndName}
                                            size="extra-small-plus"
                                        />
                                    ) : (
                                        <>
                                            <WorkItemTypeIcon
                                                className="margin-left-8"
                                                data={getWorkItemTypeIconData(item.parent?.value, workItemTypeColorAndIcons)}
                                                suppressTooltip
                                            />
                                            {item.parent &&
                                                taskboardService.isStateComplete(item.parent.value) &&
                                                (notStartedString || inProgressString) && (
                                                    <Icon
                                                        className="margin-left-8 taskboard-row-warning-icon"
                                                        iconName="IncidentTriangle"
                                                        tooltipProps={{
                                                            text: format(
                                                                Resources.ActiveChildWarning,
                                                                item.parent.value.getFieldValue(CoreFieldRefNames.WorkItemType),
                                                                item.parent.value.getFieldValue(CoreFieldRefNames.State)
                                                            )
                                                        }}
                                                    />
                                                )}
                                            <span className="margin-left-8">
                                                {orderByAssignedTo
                                                    ? Resources.Unassigned
                                                    : item.parent?.value.getFieldValue(CoreFieldRefNames.Title) || Resources.Unparented}
                                            </span>
                                        </>
                                    )}
                                    {(notStartedString || inProgressString) && (
                                        <div className="taskboard-state-rollup flex-row">
                                            {notStartedString && <span>{notStartedString}</span>}
                                            {inProgressString && <span className={css(notStartedString && "margin-left-8")}>{inProgressString}</span>}
                                        </div>
                                    )}
                                </div>
                            </td>
                        </tr>
                    );
                } else {
                    return (
                        <Observer
                            rowChildren={{
                                observableValue: item.children,
                                filter: (parent: ITaskboardWorkItem, action: string) => {
                                    // Allow the row to rerender before measuring the height.
                                    window.setTimeout(() => {
                                        setRowHeight();
                                    }, 0);
                                    const isRemoveWorkItemAction = action === WorkItemArrayEvents.RemoveWorkItem;

                                    return !(isCardMovementOptimized && isRemoveWorkItemAction);
                                }
                            }}
                            rowParent={{
                                observableValue: item.parent,
                                filter: (parent: ITaskboardWorkItem, action: string) => {
                                    return !(action === WorkItemEvents.ComputedStyleUpdated);
                                }
                            }}
                        >
                            {() => {
                                let parentRemainingWork = 0;
                                let parentRemainingWorkText = "";
                                if (taskboardData?.taskboardModel?.workRollupField) {
                                    item.children.value.forEach(child => {
                                        const childRemainingWork = child.value.getFieldValue<string>(taskboardData!.taskboardModel.workRollupField);
                                        if (childRemainingWork) {
                                            const skipItem = taskboardService.needToSkipThisChildTaskForRemainingHoursCalculation(child);
                                            parentRemainingWork += !skipItem ? parseFloat(childRemainingWork) : 0;
                                        }
                                    });
                                }
                                if (parentRemainingWork) {
                                    parentRemainingWorkText = formatRemainingWorkForDisplay(parentRemainingWork);
                                }
                                return (
                                    <tr key={rowIndex} className="full-height" data-row-index={rowIndex} ref={rowRef}>
                                        {workItemColumnIdMap.value &&
                                            columns &&
                                            columns.map((column, index) => {
                                                const rowId = orderByAssignedTo
                                                    ? item.assignedTo?.displayName ?? unassignedId
                                                    : item.parent?.value?.id?.toString() ?? unparentedId.toString();
                                                if (index === 0) {
                                                    return (
                                                        <td colSpan={2} className="taskboard-parent-cell" key="taskboard-parent-cell">
                                                            <div className="flex-row full-height">
                                                                <div>
                                                                    <Button
                                                                        ariaLabel={Resources.CollapseRow}
                                                                        className="taskboard-expand-collapse-button"
                                                                        iconProps={{
                                                                            iconName: "TriangleSolidDown12",
                                                                            size: IconSize.small,
                                                                            className: "icon-extra-small"
                                                                        }}
                                                                        onClick={() => {
                                                                            item.expanded.value = false;
                                                                            someRowsExpanded.value = filteredTaskboardRows.value.some(
                                                                                row => row.expanded.value
                                                                            );
                                                                        }}
                                                                        subtle
                                                                    />
                                                                </div>
                                                                <Observer newItemType={newItemType}>
                                                                    {(props: { newItemType: string }) => {
                                                                        const isNewParent =
                                                                            !!props.newItemType && rowIndex === 0 && (item.parent?.value.id || 0) < 0;
                                                                        item.parent?.updateBackgroundColor(pageContext, cardStyles);
                                                                        if (orderByAssignedTo) {
                                                                            return (
                                                                                <div className="assigned-to-cell">
                                                                                    <IdentityView
                                                                                        displayTooltipOnHover={true}
                                                                                        identityRef={item.assignedTo}
                                                                                        className="margin-top-8 text-ellipsis"
                                                                                        viewMode={IdentityDisplayMode.AvatarAndName}
                                                                                        size="small"
                                                                                    />
                                                                                    {parentRemainingWorkText && (
                                                                                        <Tooltip overflowOnly>
                                                                                            <div className="remaining-work font-weight-semibold flex-self-end padding-8 text-ellipsis">
                                                                                                {parentRemainingWorkText}
                                                                                            </div>
                                                                                        </Tooltip>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        } else {
                                                                            return (
                                                                                <div>
                                                                                    <TaskboardCard
                                                                                        workItem={item.parent!}
                                                                                        isNewItem={isNewParent}
                                                                                        onCardChange={onCardChange}
                                                                                        onCardClick={onCardClick}
                                                                                        onCardDrag={onCardDrag}
                                                                                        onCardFocus={onRowFocus}
                                                                                        onKeyDown={(event, itemClicked) =>
                                                                                            onParentCardKeyDown(event, itemClicked, item, rowIndex)
                                                                                        }
                                                                                        onNewItemCreate={(enterPressed, id) => onParentItemCreate(id)}
                                                                                        onNewItemCancel={onParentItemCancel}
                                                                                        rowId={rowId}
                                                                                        scrollableData={scrollableData}
                                                                                        taskboardData={taskboardData}
                                                                                        workItemTypeIconData={getWorkItemTypeIconData(
                                                                                            item.parent?.value,
                                                                                            workItemTypeColorAndIcons
                                                                                        )}
                                                                                        remainingWorkText={parentRemainingWorkText}
                                                                                        ignoreRemainingWorkFieldValue={true}
                                                                                        canChangeRemainingWork={false}
                                                                                    />
                                                                                </div>
                                                                            );
                                                                        }
                                                                    }}
                                                                </Observer>
                                                            </div>
                                                        </td>
                                                    );
                                                }
                                                return (
                                                    <Observer itemChildren={item.children} workItemColumnIdMap={workItemColumnIdMap} key={index}>
                                                        {() => {
                                                            const children = item.children.value.filter(
                                                                child => workItemColumnIdMap.value[child.value?.id] === column.id
                                                            );
                                                            const columnId = column.id;
                                                            const childTypes = taskboardData?.taskboardModel?.childWorkItemTypes?.filter(childType =>
                                                                taskboardData.taskboardModel.enabledWorkItemTypes.some(
                                                                    enabledType => enabledType == childType
                                                                )
                                                            );
                                                            return (
                                                                <td
                                                                    className="taskboard-expanded-cell"
                                                                    key={columnId}
                                                                    data-column={columnId}
                                                                    data-columnname={column.name}
                                                                    data-row={rowId}
                                                                >
                                                                    <div className="flex-column full-height">
                                                                        <div className="flex-row flex-wrap">
                                                                            {children.map(child => {
                                                                                const originalId = child.value.id;
                                                                                if (!isCardSettingsOptimizationEnabled) {
                                                                                    child.updateBackgroundColor(pageContext, cardStyles);
                                                                                }
                                                                                return (
                                                                                    <ConditionalChildren
                                                                                        key={child.value.id}
                                                                                        renderChildren={child.value.filteredOut}
                                                                                        inverse={true}
                                                                                    >
                                                                                        <Observer
                                                                                            newChildId={{
                                                                                                observableValue: newChildId,
                                                                                                filter: () => originalId < 0
                                                                                            }}
                                                                                        >
                                                                                            {(props: { newChildId: number | undefined }) => {
                                                                                                const isNewChild =
                                                                                                    props.newChildId === child.value.id;
                                                                                                return (
                                                                                                    <TaskboardCard
                                                                                                        key={child.value.id}
                                                                                                        workItem={child}
                                                                                                        isNewItem={isNewChild}
                                                                                                        onCardChange={onCardChange}
                                                                                                        onCardClick={onCardClick}
                                                                                                        onCardDrag={onCardDrag}
                                                                                                        onCardFocus={onRowFocus}
                                                                                                        onKeyDown={(event, itemClicked) =>
                                                                                                            onChildCardKeyDown(
                                                                                                                event,
                                                                                                                itemClicked,
                                                                                                                item,
                                                                                                                rowIndex
                                                                                                            )
                                                                                                        }
                                                                                                        columnId={columnId}
                                                                                                        rowId={rowId}
                                                                                                        scrollableData={scrollableData}
                                                                                                        showChildRollup={false}
                                                                                                        onNewItemCreate={
                                                                                                            isNewChild
                                                                                                                ? (enterPressed: boolean) => {
                                                                                                                      onChildItemCreate(
                                                                                                                          item,
                                                                                                                          child,
                                                                                                                          originalId,
                                                                                                                          enterPressed
                                                                                                                      );
                                                                                                                  }
                                                                                                                : undefined
                                                                                                        }
                                                                                                        onNewItemCancel={
                                                                                                            isNewChild
                                                                                                                ? () => {
                                                                                                                      onChildItemCancel(child);
                                                                                                                  }
                                                                                                                : undefined
                                                                                                        }
                                                                                                        taskboardData={taskboardData}
                                                                                                        workItemTypeIconData={getWorkItemTypeIconData(
                                                                                                            child?.value,
                                                                                                            workItemTypeColorAndIcons
                                                                                                        )}
                                                                                                        canChangeRemainingWork={true}
                                                                                                    />
                                                                                                );
                                                                                            }}
                                                                                        </Observer>
                                                                                    </ConditionalChildren>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                        {index === 1 &&
                                                                            childTypes &&
                                                                            !orderByAssignedTo &&
                                                                            (childTypes.length > 1 ? (
                                                                                <>
                                                                                    <div className="flex-grow" />
                                                                                    <Dropdown
                                                                                        className="taskboard-new-item-dropdown margin-horizontal-8 margin-top-4 margin-bottom-8"
                                                                                        items={getWorkItemTypeMenuItems(
                                                                                            childTypes,
                                                                                            workItemTrackingDataProvider,
                                                                                            pageContext,
                                                                                            () => {}
                                                                                        )}
                                                                                        renderExpandable={DropdownExpandableButton}
                                                                                        onSelect={(event, listItem) =>
                                                                                            onNewChildSelected(listItem.id, item)
                                                                                        }
                                                                                        selection={readonlySelection.current}
                                                                                    />
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <div className="flex-grow" />
                                                                                    <Button
                                                                                        iconProps={{ iconName: "Add" }}
                                                                                        className="taskboard-new-item-button"
                                                                                        ariaLabel={Resources.NewItem}
                                                                                        onClick={() => onNewChildSelected(childTypes[0], item)}
                                                                                    />
                                                                                </>
                                                                            ))}
                                                                    </div>
                                                                </td>
                                                            );
                                                        }}
                                                    </Observer>
                                                );
                                            })}
                                    </tr>
                                );
                            }}
                        </Observer>
                    );
                }
            }}
        </Observer>
    );
};

function getWorkItemStateRollupCount(workItems: IObservableClientWorkItem[], taskboardData: ISprintTaskboardData): IWorkItemStatusRollup {
    let completed = 0;
    let inProgress = 0;
    let notStarted = 0;

    const taskWorkItemTypesToStateMap = taskboardData.taskboardModel.taskWorkItemTypesToStateMap;

    workItems.forEach(workItem => {
        const workItemState = workItem.value.getFieldValue<string>(CoreFieldRefNames.State);
        const workItemType = workItem.value.getFieldValue<string>(CoreFieldRefNames.WorkItemType);
        if (workItemState && workItemType && taskWorkItemTypesToStateMap[workItemType]) {
            const stateValue = taskWorkItemTypesToStateMap[workItemType][workItemState];
            switch (stateValue) {
                case WorkItemStateCategory.Proposed:
                    notStarted++;
                    break;
                case WorkItemStateCategory.InProgress:
                    inProgress++;
                default:
                    completed++;
            }
        }
    });

    return {
        completed,
        inProgress,
        notStarted
    };
}

function getWorkItemTypeIconData(item?: IClientWorkItem, workItemTypeColorAndIcons?: WorkItemTypeColorAndIcon[]) {
    if (item && workItemTypeColorAndIcons) {
        return workItemTypeColorAndIcons.find(iconData => iconData.workItemTypeName === item.getFieldValue(CoreFieldRefNames.WorkItemType));
    }
}
